import React, {Component, FormEvent, FocusEvent} from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import {omit} from "lodash"
import {Col, FormControl} from "react-bootstrap"

import id from "@irati/utils/id"
import {ValidationInput} from "@irati/utils/validation"
import {InjectIntl, injectIntl, getResolvedMessage} from "@irati/utils/lang"
import {ValidationProps} from "@irati/utils/validation/Input"

import {FormGroup} from "../Form"
import ErrorText from "../ErrorText"
import IconText from "../IconText"
import {IconTypes} from "../Icon"
import RichEditorWrapper from "./RichEditorWrapper"

import Label from "./styled/Label"
import ExtraContentContainer from "./styled/ExtraContentContainer"

interface Props extends InjectIntl, ValidationProps {
  className?: string
  hideLabel?: boolean
  hint?: string
  maxLength?: number
  includeCharCountdown?: boolean
  onBlur?: (e: FocusEvent<FormControl>, value?: string) => void
  type?: string
  value?: string
  textReplaceTypes?: string[]
  label?: string
  name: string
  onChange: (value: string) => void
  ExtraActions?: () => JSX.Element
  ExtraContent?: () => JSX.Element
  HeaderContent?: () => JSX.Element
}

interface State {
  errorMsg?: string
  value?: string
  blurred: boolean
  renderRichEditor: boolean
}

export class FormItemTextArea extends Component<Props, State> {
  validation: ValidationInput
  inputRef?: HTMLInputElement

  static contextTypes = {
    currentScreen: PropTypes.string
  }

  static defaultProps: Omit<Props, "intl" | "formatMessage" | "label" | "onChange" | "name"> = {
    className: "",
    hideLabel: false,
    onBlur: () => undefined,
    textReplaceTypes: [],
    type: "textarea",
    includeCharCountdown: true
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      renderRichEditor: false,
      value: props.value,
      blurred: false
    }

    this.inputRef = undefined
    this.validation = new ValidationInput(this)
  }

  componentDidMount() {
    if (this.inputRef) {
      this.setState({renderRichEditor: true})
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.state.blurred) {
      this.validation.update(prevProps, prevState)
    }
  }

  componentWillUnmount() {
    this.validation.destroy()
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    this.setState({value: nextProps.value})
  }

  handleBlur = (e: FocusEvent<FormControl>) => {
    this.setState({blurred: true})
    this.validation.update()
    this.props.onBlur!(e, this.state.value)
  }

  handleChange = (e: FormEvent<FormControl>) => {
    let {value} = e.target
    if (value === "") {
      value = null
    }
    this.setState({value})
    this.props.onChange(value)
  }

  render() {
    const {hideLabel, hint, maxLength, name, textReplaceTypes, type, includeCharCountdown, ExtraContent, HeaderContent} = this.props
    const {errorMsg, renderRichEditor} = this.state
    let {value} = this.state

    const idNamespace = id("textarea", this.props)
    const inputId = id("input", idNamespace) || name
    const className = classNames("perkboxInput", this.props.className)

    if (!value) {
      // if null/undefined => use ""
      value = ""
    }

    const inputProps = omit(this.props, [
      "className",
      "formatMessage",
      "hideLabel",
      "hint",
      "id",
      "label",
      "onBlur",
      "onChange",
      "textReplaceTypes",
      "translate",
      "validation",
      "value",
      "maxLength",
      "includeCharCountdown"
    ])
    const hasRichEditor = (renderRichEditor && textReplaceTypes!.length > 0) || (maxLength && includeCharCountdown)
    return (
      <FormGroup
        id={idNamespace}
        className={className}
        controlId={inputId}
        validationState={errorMsg ? "error" : undefined}
        flush={this.props.flush}>
        <Col hidden={hideLabel} xs={12}>
          <Label id={id("label", idNamespace)} error={errorMsg} htmlFor={inputId}>
            {getResolvedMessage(this, "label")}
          </Label>
        </Col>
        <Col hidden={!HeaderContent} xs={12}>
          {HeaderContent && <HeaderContent />}
        </Col>
        <Col xs={12}>
          <FormControl
            {...inputProps}
            inputRef={(ref) => {
              this.inputRef = ref
              if (inputProps.inputRef) {
                inputProps.inputRef(ref)
              }
            }}
            componentClass="textarea"
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            placeholder={getResolvedMessage(this, "placeholder")}
            style={hint || hasRichEditor ? {marginBottom: 0} : {}}
            type={type}
            value={value}
          />
          {ExtraContent && (
            <ExtraContentContainer>
              <ExtraContent />
            </ExtraContentContainer>
          )}
          {hasRichEditor && (
            <RichEditorWrapper
              hasError={!!errorMsg}
              inputRef={this.inputRef}
              onChange={this.handleChange}
              textReplaceTypes={textReplaceTypes!}
              value={value}
              maxLength={maxLength}
              ExtraActions={this.props.ExtraActions}
            />
          )}
          {errorMsg && (!maxLength || !includeCharCountdown) && (
            <div>
              <ErrorText id={id("error-copy", idNamespace)} small>
                {getResolvedMessage(this, "errorMsg", this.state)}
              </ErrorText>
            </div>
          )}
          {hint && (
            <div>
              <IconText icon={IconTypes.info} id={id("hint-copy", idNamespace)} small>
                {hint}
              </IconText>
            </div>
          )}
        </Col>
      </FormGroup>
    )
  }
}

export default injectIntl(FormItemTextArea)
